import {
  Box,
  List,
  Typography,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  Skeleton,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Devices as DevicesIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Application } from '../types/application';
import { collection, query, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Timestamp } from 'firebase/firestore';
import STATUS_COLORS from '../../../constants/statusColors';

interface Props {
  onSelect: (application: Application) => void;
  selected: Application | null;
}

// 📅 날짜 포맷팅 유틸 함수 (OrdersTable과 통일)
const formatDateTime = (timestamp: Timestamp | { seconds: number } | undefined): string => {
  if (!timestamp) return '-';
  
  try {
    let date: Date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return '-';
    }
    
    return date.toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.warn('날짜 포맷팅 오류:', error);
    return '-';
  }
};

// 📱 기기 표시 포맷팅
const formatDeviceDisplay = (devices: Application['devices']): { display: string; count: number } => {
  if (!devices || !Array.isArray(devices) || devices.length === 0) {
    return { display: '기기 없음', count: 0 };
  }
  
  const deviceModels = devices
    .map(d => d.deviceModel)
    .filter(Boolean);
    
  if (deviceModels.length === 0) {
    return { display: '기기 없음', count: 0 };
  }
  
  const mainDevice = deviceModels[0];
  const extraCount = deviceModels.length - 1;
  const display = extraCount > 0 ? `${mainDevice} 외 ${extraCount}건` : mainDevice;
  
  return { display, count: deviceModels.length };
};

// 🔍 검색 필터 함수
const filterApplications = (applications: Application[], searchTerm: string): Application[] => {
  if (!searchTerm.trim()) return applications;
  
  const term = searchTerm.toLowerCase().trim();
  
  return applications.filter(app => {
    // 기본 정보 검색
    const basicMatch = [
      app.user_name,
      app.phone_number,
      app.application_number,
      app.store_code
    ].some(field => field?.toString().toLowerCase().includes(term));
    
    // 기기 정보 검색
    const deviceMatch = app.devices?.some(device => 
      device.deviceModel?.toLowerCase().includes(term) ||
      device.deviceType?.toLowerCase().includes(term)
    );
    
    // 상태 검색
    const statusMatch = app.status?.toLowerCase().includes(term);
    
    return basicMatch || deviceMatch || statusMatch;
  });
};

// 🎨 스켈레톤 로딩 컴포넌트
const ApplicationSkeleton = () => (
  <Paper sx={{ p: 2, borderRadius: 3, mb: 1 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box flex={1}>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mt: 0.5 }} />
        <Skeleton variant="text" width="70%" height={20} sx={{ mt: 0.5 }} />
        <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
      </Box>
      <Skeleton variant="rounded" width={60} height={24} sx={{ ml: 2 }} />
    </Box>
  </Paper>
);

const AlertsSidebar = ({ onSelect, selected }: Props) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // 🔥 실시간 데이터 구독 with 에러 처리
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    const q = query(
      collection(db, 'applications'), 
      orderBy('created_at', 'desc'),
      limit(100) // 성능 최적화: 최근 100건만
    );
    
    const unsubscribe = onSnapshot(
      q, 
      (snapshot) => {
        try {
          const result: Application[] = snapshot.docs.map((doc) => {
            const data = doc.data() as Omit<Application, 'id'>;
            return {
              ...data,
              id: doc.id,
            };
          });
          
          setApplications(result);
          setError(null);
        } catch (err) {
          console.error('데이터 파싱 오류:', err);
          setError('데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Firestore 구독 오류:', err);
        setError('실시간 데이터 연결에 실패했습니다.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // 🔍 필터링된 데이터 메모이제이션
  const filteredApplications = useMemo(() => {
    return filterApplications(applications, search);
  }, [applications, search]);

  // 🎯 이벤트 핸들러들
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearch('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, app: Application) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(app);
    }
  }, [onSelect]);

  // 📊 통계 정보
  const stats = useMemo(() => {
    const total = applications.length;
    const filtered = filteredApplications.length;
    return { total, filtered, isFiltered: search.trim().length > 0 };
  }, [applications.length, filteredApplications.length, search]);

  return (
    <Box 
      sx={{ 
        p: 2, 
        width: '100%', 
        maxHeight: '100vh', 
        overflowY: 'auto', 
        bgcolor: '#fbfbfc',
        display: 'flex',
        flexDirection: 'column'
      }}
      role="complementary"
      aria-label="신청자 목록 사이드바"
    >
      {/* 📋 헤더 */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ letterSpacing: '-1px', color: '#1976d2' }}
          >
            신청자 목록
          </Typography>
          <Badge 
            badgeContent={stats.filtered} 
            color="primary" 
            max={999}
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 700,
                fontSize: '0.75rem'
              }
            }}
          >
            <PersonIcon color="action" />
          </Badge>
        </Box>
        
        {/* 📈 통계 표시 */}
        {stats.isFiltered && (
          <Typography variant="caption" color="text.secondary">
            {stats.filtered}개 결과 (전체 {stats.total}개 중)
          </Typography>
        )}
      </Box>

      {/* 🔍 검색 필드 */}
      <TextField
        label="이름, 번호, 신청번호, 기기명 검색"
        variant="outlined"
        size="small"
        fullWidth
        value={search}
        onChange={handleSearchChange}
        placeholder="예: 홍길동, 010-1234-5678, iPhone"
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: '#f7fafd',
            '&:hover': {
              bgcolor: '#f0f7ff',
            },
            '&.Mui-focused': {
              bgcolor: '#fff',
            }
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: search && (
            <InputAdornment position="end">
              <Tooltip title="검색어 지우기">
                <IconButton
                  size="small"
                  onClick={handleSearchClear}
                  edge="end"
                  aria-label="검색어 지우기"
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        inputProps={{
          'aria-label': '신청자 검색',
        }}
      />

      <Divider sx={{ mb: 2, borderColor: '#e0e7ef' }} />

      {/* 🚨 에러 표시 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* 📋 목록 영역 */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          // 🎨 로딩 스켈레톤
          <Box>
            {Array.from({ length: 5 }).map((_, index) => (
              <ApplicationSkeleton key={index} />
            ))}
          </Box>
        ) : filteredApplications.length === 0 ? (
          // 📭 빈 상태
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            py={6}
            textAlign="center"
          >
            <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {stats.isFiltered ? '검색 결과가 없습니다' : '신청자가 없습니다'}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {stats.isFiltered 
                ? '다른 검색어로 시도해보세요' 
                : '새로운 신청을 기다리고 있습니다'
              }
            </Typography>
          </Box>
        ) : (
          // 📋 신청자 목록
          <List 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1, 
              p: 0 
            }}
            role="listbox"
            aria-label="신청자 목록"
          >
            {filteredApplications.map((app, index) => {
              const { display: deviceDisplay, count: deviceCount } = formatDeviceDisplay(app.devices);
              const isSelected = selected?.id === app.id;
              const statusColor = STATUS_COLORS[app.status] || '#757575';

              return (
                <Paper
                  key={app.id}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: isSelected
                      ? `2px solid ${statusColor}`
                      : '1.5px solid #e0e7ef',
                    bgcolor: isSelected ? '#f4faff' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isSelected
                      ? `0 4px 16px ${statusColor}20`
                      : '0 1px 4px rgba(0,0,0,0.04)',
                    '&:hover': {
                      borderColor: statusColor,
                      bgcolor: '#f8fbff',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${statusColor}15`,
                    },
                    '&:focus': {
                      outline: `2px solid ${statusColor}`,
                      outlineOffset: '2px',
                    },
                    mb: 0.5,
                  }}
                  onClick={() => onSelect(app)}
                  onKeyDown={(e) => handleKeyDown(e, app)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Box flex={1} minWidth={0}>
                      {/* 👤 사용자 정보 */}
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography 
                          variant="subtitle2" 
                          fontWeight={700} 
                          color="#222"
                          noWrap
                          title={app.user_name}
                        >
                          {app.user_name}
                        </Typography>
                        {app.application_number && (
                          <Typography variant="caption" color="primary.main" fontWeight={600}>
                            #{app.application_number}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* 📞 전화번호 */}
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {app.phone_number}
                        </Typography>
                      </Box>
                      
                      {/* 📱 기기 정보 */}
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <DevicesIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          noWrap
                          title={deviceDisplay}
                        >
                          {deviceDisplay}
                        </Typography>
                        {deviceCount > 1 && (
                          <Chip 
                            label={`${deviceCount}개`} 
                            size="small"
                            variant="outlined"
                            sx={{ 
                              height: 18, 
                              fontSize: '0.7rem',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                      
                      {/* 📅 신청일시 */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <ScheduleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                          {formatDateTime(app.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* 🏷️ 상태 칩 */}
                    <Tooltip title={`현재 상태: ${app.status}`}>
                      <Chip
                        label={app.status}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          color: '#fff',
                          bgcolor: statusColor,
                          minWidth: 64,
                          letterSpacing: '-0.5px',
                          boxShadow: `0 2px 8px ${statusColor}25`,
                          border: 'none',
                          fontSize: '0.75rem',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: `0 4px 12px ${statusColor}35`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Paper>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default AlertsSidebar;
